import { useCallback, useState } from 'react';
import { useLocalization } from '@assetron/common/contexts/localization/LocalizationContext';
import {
  API_ERROR_CODES,
  EMPTY_STRING,
} from '@assetron/common/constants/constant';
import useAuth from '@assetron/common/hooks/useAuth';
import { useNavigation } from '../../navigation/useNavigation';
import SuccessIcon from '@assetron/web-common/icons/SuccessIcon';
import { Box, Typography } from '@mui/material';
import AssetronButton from '@assetron/web-common/components/common/AssetronButton';
import useResetPasswordSuccessContent from './styles/useResetPasswordSuccessContent';
import useDocumentTitle from '@assetron/web-common/components/common/useDocumentTitle';
import useSignupData from '@assetron/common/hooks/useSignupData';
import { postIdTokens } from '@assetron/common/api';
import { useMutation } from '@tanstack/react-query';
import apiErrorHandler from '@assetron/common/utils/apiErrorHandler';
import { IS_USER_LOGGED_IN } from '@assetron/web-common/constants/LocalStorageConstants';
import setSameSiteCookieWithoutExpiry from '@assetron/common/utils/setSameSiteCookieWithoutExpiry';
import checkAndHandlePendingCred from 'auth/src/utils/auth-helper/checkAndHandlePendingCred';
import { GA_EVENTS } from '@assetron/common/constants/googleAnalyticsEvents';
import { trackGaEvent } from '@assetron/common/utils/googleAnalytics';

function ResetPasswordSuccessContent() {
  const { t } = useLocalization();
  useDocumentTitle(t('resetPassword.pageTitle'));
  const styles = useResetPasswordSuccessContent();
  const { userEmail, userNewPassword, setUserNewPassword, clearInfo } =
    useSignupData();
  const { signInWithEmailPassword } = useAuth();
  const { navigateToMainApp, navigateToSignInLanding } = useNavigation();
  const [isLoading, setIsLoading] = useState(false);

  const postTokens = useMutation({
    mutationFn: postIdTokens,
    onSuccess: () => {
      setUserNewPassword(EMPTY_STRING);
      setIsLoading(false);
    },
    onError: (error) => {
      apiErrorHandler(
        error,
        {
          [API_ERROR_CODES.BAD_REQUEST]: t('authErrors.invalidIdToken'),
        },
        t('apiErrorText'),
      );
      clearInfo();
      navigateToSignInLanding();
    },
    retry: 3,
  });

  const handleOnClick = useCallback(() => {
    trackGaEvent(GA_EVENTS.AUTH_RESET_PASSWORD_CONTINUE_CLICK, {
      source: 'reset_password_success',
    });
    setIsLoading(true);
    signInWithEmailPassword(userEmail, userNewPassword)
      .then(async (res) => {
        const idToken = await res?.user?.getIdToken();
        await postTokens.mutateAsync({
          idToken: idToken ?? EMPTY_STRING,
          refreshToken: res.user?.refreshToken ?? EMPTY_STRING,
        });
        await checkAndHandlePendingCred(res);
        setSameSiteCookieWithoutExpiry(IS_USER_LOGGED_IN, 'true');
        trackGaEvent(GA_EVENTS.AUTH_RESET_PASSWORD_CONTINUE_SUCCESS, {
          method: 'email_password',
        });
        navigateToMainApp();
      })
      .catch(() => {
        trackGaEvent(GA_EVENTS.AUTH_RESET_PASSWORD_CONTINUE_FAILURE, {
          failure_reason: 'post_reset_signin_failed',
        });
        clearInfo();
        navigateToSignInLanding();
      });
  }, [
    signInWithEmailPassword,
    userEmail,
    userNewPassword,
    postTokens,
    navigateToMainApp,
    clearInfo,
    navigateToSignInLanding,
  ]);

  return (
    <Box sx={styles.root} data-testid="reset-password-success-screen">
      <Box sx={styles.textContainer}>
        <SuccessIcon />
      </Box>
      <Box sx={styles.title}>
        <Box>
          <Typography
            variant="large_heading_30_semibold"
            sx={styles.resetTitle}
            data-testid="reset-password-success-title"
          >
            {t('resetPasswordSuccess.successText')}
          </Typography>
        </Box>
        <Typography variant="body_14_regular" sx={styles.resetDescription}>
          {t('resetPasswordSuccess.resetSuccessText')}
        </Typography>
      </Box>
      <Box sx={styles.buttonContainer}>
        <AssetronButton
          hierarchy="primary"
          size="md"
          onClick={handleOnClick}
          isLoading={isLoading}
          dataTestId="reset-password-success-continue-button"
        >
          {t('resetPasswordSuccess.buttonText')}
        </AssetronButton>
      </Box>
    </Box>
  );
}

export default ResetPasswordSuccessContent;
